import { useEffect, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Building2, ArrowUpRight, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Ring } from "@/components/Primitives";

const TIERS = ["Standard", "Silver", "Gold", "Platinum", "Enterprise"];
const EMPTY = { name: "", industry: "", country: "", tier: "Standard", primary_contact_name: "", primary_contact_email: "" };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/clients").then(r => setItems(asArray(r.data))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", { ...form, name: form.name.trim() });
      toast.success(`${data.name} added`);
      setForm(EMPTY); setCreating(false);
      nav(`/clients/${data.id}`); // land on the company so branches can be added right away
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Clients</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Customer Portfolio</h1>
        </div>
        <button onClick={() => setCreating(true)} data-testid="client-new" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><Plus size={15} /> New company</button>
      </div>

      {items.length === 0 ? (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-10 text-center">
          <Building2 size={28} className="text-zinc-600 mx-auto mb-3" />
          <div className="text-zinc-300">No companies yet.</div>
          <p className="text-sm text-zinc-500 mt-1">Add a company, then open it to add its branches and onboard endpoints.</p>
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><Plus size={15} /> Add your first company</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(c => (
            <Link key={c.id} to={`/clients/${c.id}`} data-testid={`client-card-${c.id}`} className="bg-zinc-950 border border-zinc-900 rounded-md p-5 hover:border-zinc-800 transition-colors group">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2"><Building2 size={14} className="text-blue-400" /><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{c.industry || "—"}</div></div>
                  <h2 className="font-display text-xl font-semibold tracking-tight mt-1">{c.name}</h2>
                  <div className="text-xs text-zinc-500 mt-0.5">{[c.country, c.tier && `Tier ${c.tier}`].filter(Boolean).join(" · ")}</div>
                </div>
                <ArrowUpRight size={14} className="text-zinc-700 group-hover:text-zinc-300" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <Ring value={c.health_score ?? 0} label="Health" size={64} />
                <div className="text-right space-y-1">
                  <div className="text-xs text-zinc-500">Branches <span className="font-mono text-zinc-200 ml-2">{c.branch_count ?? c.site_count ?? 0}</span></div>
                  <div className="text-xs text-zinc-500">Contact <span className="font-mono text-zinc-200 ml-2">{c.primary_contact_name || "—"}</span></div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-6" onClick={() => setCreating(false)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
              <h2 className="font-display text-lg font-semibold">New company</h2>
              <button onClick={() => setCreating(false)} className="p-1.5 rounded hover:bg-zinc-900 text-zinc-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Company name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="nc-name" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Industry</label>
                  <input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} data-testid="nc-industry" placeholder="e.g. Real Estate" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Country</label>
                  <input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="nc-country" placeholder="e.g. Philippines" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Tier</label>
                  <select value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })} data-testid="nc-tier" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                    {TIERS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Primary contact</label>
                  <input value={form.primary_contact_name} onChange={e => setForm({ ...form, primary_contact_name: e.target.value })} data-testid="nc-contact" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Contact email</label>
                <input value={form.primary_contact_email} onChange={e => setForm({ ...form, primary_contact_email: e.target.value })} data-testid="nc-email" type="email" className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => setCreating(false)} className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm rounded px-4 py-2">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="nc-save" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-4 py-2">{saving ? "Creating…" : "Create company"}</button>
              </div>
              <p className="text-[11px] text-zinc-500">After creating, you'll land on the company page where you can add its <strong className="text-zinc-300">branches</strong> and onboard endpoints.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
